import {ArrowBack} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    Paper,
    Typography,
    useTheme
} from '@mui/material';
import {usePostHog} from '@posthog/react';
import {useSnackbar} from 'notistack';
import { TrackedTextField } from 'src/theme/StyledFields';
import React, {useContext, useEffect, useState} from 'react';
import ReactMarkdown from 'react-markdown';
import {Link, useNavigate, useSearchParams} from 'react-router-dom';
import {capture} from '../analytics/posthog';
import ContentScreenshotThumbnail from '../components/ContentScreenshotThumbnail';
import MarkdownEditor from '../components/MarkdownEditor';
import ResponseGraphs from '../components/ResponseGraphs';
import ApiClient from '../config/ApiClient';
import {useRoleLabels} from '../hooks/useRoleLabels';
import {CoachContent} from '../object-actions/types/types';
import {ThemeContext} from '../theme/ThemeContext';
import {
    getDeliveryStatus,
    markAthleteReceived,
    markParentReceived,
    publishCoachContent,
    regenerateWithChanges
} from '../utils/contentHelpers';
import {DateViewCalendar} from "./CoachContentReadOnlyView";

interface CoachContentScreenProps {
    entity: CoachContent;
}

const CoachContentEditorView: React.FC<CoachContentScreenProps> = ({
                                                                       entity
                                                                   }) => {
    const theme = useTheme();
    const posthog = usePostHog();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const {enqueueSnackbar} = useSnackbar();
    const {brandingSettings} = useContext(ThemeContext);

    // State for editorial features
    const [isEditMode, setIsEditMode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
    const [changeRequest, setChangeRequest] = useState('');

    // Edit form state
    const [editTitle, setEditTitle] = useState(entity.title);
    const [editBody, setEditBody] = useState(entity.body);

    // Check for edit query parameter on mount
    useEffect(() => {
        const editParam = searchParams.get('edit');
        if (editParam === 'manual' || editParam === 'true') {
            setIsEditMode(true);
        } else if (editParam === 'prompt' || editParam === 'ai') {
            setRegenerateDialogOpen(true);
        }
    }, [searchParams]);

    // Sync edit form state when entity prop changes (e.g., after navigation to new version)
    useEffect(() => {
        setEditTitle(entity.title);
        setEditBody(entity.body);
        // Only reset edit mode when entity changes if not coming from query param
        const editParam = searchParams.get('edit');
        if (editParam !== 'manual' && editParam !== 'true' && editParam !== 'prompt' && editParam !== 'ai') {
            setIsEditMode(false);
        }
    }, [entity.id, entity.title, entity.body, searchParams]);

    // Delivery status
    const deliveryStatus = getDeliveryStatus(entity);


    // Mark as received when athlete or parent accesses the page (only if not already marked)
    useEffect(() => {
        const markAsReceived = async () => {
            try {
                if (entity.my_roles?.includes('athlete') && !entity.athlete_received) {
                    await markAthleteReceived(Number(entity.id));
                } else if (entity.my_roles?.includes('parent') && !entity.parent_received) {
                    await markParentReceived(Number(entity.id));
                }
            } catch (error) {
                console.error('Error marking content as received:', error);
                // Don't show error to user as this is a background operation
            }
        };

        markAsReceived();
    }, [entity.my_roles, entity.id, entity.athlete_received, entity.parent_received]);

    if (!entity || entity._type !== 'CoachContent') return <LinearProgress/>;

    const handlePublish = async () => {
        capture(posthog, "coach_content_publish_clicked", {content_id: entity.id, content_type: entity.purpose});
        setLoading(true);
        try {
            await publishCoachContent(Number(entity.id));
            enqueueSnackbar('Content published successfully!', {variant: 'success'});
            // Refresh the page to show updated status
            window.location.reload();
        } catch (error) {
            enqueueSnackbar('Failed to publish content', {variant: 'error'});
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        capture(posthog, "coach_content_save_clicked", {content_id: entity.id, content_type: entity.purpose});
        setLoading(true);
        try {
            // Create a new version instead of updating (updates are disabled)
            const response = await ApiClient.post('/api/coach-content/', {
                title: editTitle,
                body: editBody, // Save as Markdown directly
                submission: entity.submission?.id || null,
                assignment: entity.assignment?.id || null,
                athlete: entity.athlete?.id || null,
                assessment: entity.assessment?.id || null,
                purpose: entity.purpose,
            });

            if (response.error) {
                throw new Error(response.error);
            }

            enqueueSnackbar('New version created successfully!', {variant: 'success'});
            setIsEditMode(false);
            // Navigate to the new version
            const newContent = response.data as CoachContent;
            if (newContent?.id) {
                navigate(`/coach-content/${newContent.id}/`);
            } else {
                // Fallback: refresh the page
                window.location.reload();
            }
        } catch (error) {
            enqueueSnackbar('Failed to save content', {variant: 'error'});
        } finally {
            setLoading(false);
        }
    };

    const handleRegenerateDraft = async () => {
        if (!changeRequest.trim()) return;
        capture(posthog, "coach_content_regenerate_clicked", {content_id: entity.id, content_type: entity.purpose});
        setLoading(true);
        try {
            // Call the regenerate_with_changes endpoint to create a new version
            const newContent = await regenerateWithChanges(Number(entity.id), changeRequest);

            setRegenerateDialogOpen(false);
            setChangeRequest('');
            enqueueSnackbar('New version created successfully!', {variant: 'success'});

            // Navigate to the new version
            if (newContent?.id) {
                setIsEditMode(false);
                navigate(`/coach-content/${newContent.id}/`);
            } else {
                // Fallback: refresh the page
                window.location.reload();
            }
        } catch (error: any) {
            const errorMessage = error?.message || 'Failed to regenerate content';
            enqueueSnackbar(errorMessage, {variant: 'error'});
        } finally {
            setLoading(false);
        }
    };

    const handleRepublishCurriculum = async () => {
        if (!entity.source_content?.id) return;
        capture(posthog, "coach_content_republish_curriculum_clicked", {content_id: entity.id});
        setLoading(true);
        try {
            const newContent = await publishCoachContent(Number(entity.source_content.id));
            enqueueSnackbar('Curriculum republished successfully! Lesson plan will be regenerated.', {variant: 'success'});
            // Navigate to the new version
            if (newContent?.id) {
                setIsEditMode(false);
                navigate(`/coach-content/${newContent.id}/`);
            } else {
                navigate(`/program-progress/${entity.assignment?.id}/`);
            }
        } catch (error) {
            enqueueSnackbar('Failed to republish curriculum', {variant: 'error'});
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container maxWidth="lg" sx={{py: 4}} data-testid={`coach-content-editor--${entity.purpose}`}>
            {entity.assignment?.id && (
                <Box sx={{mb: 2, display: 'flex', alignItems: 'center', gap: 1}}>
                    <IconButton
                        component={Link}
                        to={`/program-progress/${entity.assignment.id}`}
                        color="secondary"
                        aria-label="Back to progress report"
                    >
                        <ArrowBack/>
                    </IconButton>
                    <Typography component={Link} to={`/program-progress/${entity.assignment.id}`} variant="body2"
                                color="text.secondary"
                                sx={{textDecoration: 'none', '&:hover': {textDecoration: 'underline'}}}>
                        Back to progress report
                    </Typography>
                </Box>
            )}
            <Paper elevation={0} sx={{p: 2, borderRadius: 2}}>
                {/* Alert for failed lesson plan with source content */}
                {entity.purpose === 'lesson_plan' && !entity.json_body && entity.source_content && (
                    <Alert
                        severity="warning"
                        sx={{mb: 3}}
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                onClick={handleRepublishCurriculum}
                                disabled={loading}
                                data-attr="coach-content-editor_republish-curriculum"
                            >
                                {loading ? 'Republishing...' : 'Republish Curriculum'}
                            </Button>
                        }
                    >
                        This lesson plan failed to generate properly. You can republish the curriculum to regenerate it.
                    </Alert>
                )}

                {/* Header */}
                <Box sx={{
                    mb: 4,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap:2
                }}>
                    <Box sx={{display: 'flex', gap: 2, alignItems: 'flex-start', flexGrow:1, flex: 1}}>
                        <ContentScreenshotThumbnail
                            screenshotLight={entity.screenshot_light}
                            screenshotDark={entity.screenshot_dark}
                            alt={`${entity.title} preview`}
                            data-attr="coach-content-editor_screenshot-thumbnail"
                        />
                            {isEditMode ? <TrackedTextField
                                    fullWidth
                                    value={editTitle}
                                    onChange={(e) => setEditTitle(e.target.value)}
                                    variant="outlined"
                                    data-attr="coach-content-editor_field-title"
                                />
                                :
                                <Typography
                                    variant="h3"
                                    component="h1"
                                    gutterBottom
                                    sx={{
                                        fontWeight: 'bold',
                                        color: 'primary.main'
                                    }}>
                                    {entity.title}
                                </Typography>
                            }
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
                        {!isEditMode ? (
                            <>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        capture(posthog, "coach_content_edit_clicked", {
                                            content_id: entity.id,
                                            content_type: entity.purpose
                                        });
                                        setIsEditMode(true);
                                    }}
                                    data-attr="coach-content-editor_edit"
                                >
                                    Edit
                                </Button>
                                {!deliveryStatus.isPublished && (
                                    <Button
                                        variant="contained"
                                        color="success"
                                        onClick={handlePublish}
                                        disabled={loading}
                                        startIcon={loading ?
                                            <CircularProgress color="secondary" size={20}/> : undefined}
                                        data-attr="coach-content-editor_publish"
                                    >
                                        Publish
                                    </Button>
                                )}
                            </>
                        ) : (
                            <>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSave}
                                    disabled={loading}
                                    startIcon={loading ? <CircularProgress size={20}/> : undefined}
                                    data-attr="coach-content-editor_save"
                                >
                                    Save
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    onClick={() => setRegenerateDialogOpen(true)}
                                    sx={{ml: 1}}
                                    data-attr="coach-content-editor_open-regenerate-dialog"
                                >
                                    Request Changes
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        capture(posthog, "coach_content_cancel_clicked", {
                                            content_id: entity.id,
                                            content_type: entity.purpose
                                        });
                                        setIsEditMode(false);
                                    }}
                                    data-attr="coach-content-editor_cancel"
                                >
                                    Cancel
                                </Button>
                            </>
                        )}
                    </Box>

                    <DateViewCalendar entity={entity} />

                </Box>

                {/* ResponseGraphs Section */}
                <ResponseGraphs
                    entity={entity.athlete}
                    height={300}
                />

                {/* Content Body */}
                <Box sx={{mb: 2, mt: 2}} data-testid="coach-content-editor-body">
                    {isEditMode ? (
                        <Box>
                            <MarkdownEditor
                                value={editBody}
                                onChange={setEditBody}
                                placeholder="Enter your content here..."
                                minHeight={400}
                                data-attr="coach-content-editor_field-body"
                            />
                        </Box>
                    ) : (
                        <Box>
                            {
                                <Box
                                    sx={{
                                        '& p': {margin: 0},
                                        '& p + p': {marginTop: 2},
                                        '& h1, & h2, & h3, & h4, & h5, & h6': {
                                            marginTop: 3,
                                            marginBottom: 2,
                                            fontWeight: 'bold',
                                            color: 'text.primary',
                                            fontFamily: brandingSettings?.typography?.fontFamily || theme.typography.fontFamily
                                        },
                                        '& ul, & ol': {
                                            marginTop: 2,
                                            marginBottom: 2,
                                            paddingLeft: 3
                                        },
                                        '& li': {
                                            marginBottom: 1
                                        },
                                        '& a': {
                                            color: theme.palette.primary.main,
                                            textDecoration: 'none',
                                            '&:hover': {
                                                textDecoration: 'underline'
                                            }
                                        },
                                        '& blockquote': {
                                            borderLeft: `4px solid ${theme.palette.primary.main}`,
                                            paddingLeft: 2,
                                            marginLeft: 0,
                                            fontStyle: 'italic',
                                            color: 'text.secondary',
                                            backgroundColor: brandingSettings?.palette?.[theme.palette.mode]?.paper?.main || (theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50'),
                                            padding: 2,
                                            borderRadius: 1
                                        },
                                        '& code': {
                                            backgroundColor: brandingSettings?.palette?.[theme.palette.mode]?.paper?.main || (theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100'),
                                            padding: '2px 4px',
                                            borderRadius: 1,
                                            fontFamily: 'monospace'
                                        },
                                        '& pre': {
                                            backgroundColor: brandingSettings?.palette?.[theme.palette.mode]?.paper?.main || (theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100'),
                                            padding: 2,
                                            borderRadius: 1,
                                            overflow: 'auto'
                                        }
                                    }}
                                >
                                    <ReactMarkdown>{entity.body}</ReactMarkdown>
                                </Box>
                            }
                        </Box>
                    )}
                </Box>


                {/* Regenerate Draft Dialog */}
                <Dialog
                    open={regenerateDialogOpen}
                    onClose={() => setRegenerateDialogOpen(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Request Changes</DialogTitle>
                    <DialogContent>
                        <TrackedTextField
                            autoFocus
                            margin="dense"
                            label="Change Request"
                            placeholder="Describe what changes you'd like..."
                            fullWidth
                            multiline
                            rows={4}
                            value={changeRequest}
                            onChange={(e) => setChangeRequest(e.target.value)}
                            variant="outlined"
                            data-attr="coach-content-editor_regenerate-change-request"
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setRegenerateDialogOpen(false)}
                                data-attr="coach-content-editor_regenerate-dialog-cancel">
                            Cancel
                        </Button>
                        <Button
                            onClick={handleRegenerateDraft}
                            variant="contained"
                            disabled={!changeRequest.trim() || loading}
                            data-attr="coach-content-editor_regenerate-dialog-submit"
                        >
                            {loading ? <CircularProgress size={20}/> : 'Request Changes'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Paper>
        </Container>
    );
};

export default CoachContentEditorView;
